import { graphql, useLazyLoadQuery } from 'react-relay';
import { useMediaQuery } from 'react-responsive';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { CategoryOption } from 'components/molecules/CategoryOptionsModal/CategoryOptionsModal';
import OFFER_CATEGORIES, { Category } from 'constants/offerCategories';
import {
  PROMOTION_TAG,
  OfferType,
  CategoryDetector,
  TITLE_SUFFIX_BY_TYPE,
  buildCategories,
  filterBySearch,
  detectGamingPlatform,
  promoMatchesCategory,
} from 'utils/offers';
import type { useOffersQuery } from './__generated__/useOffersQuery.graphql';

export type { CategoryDetector } from 'utils/offers';

export type OfferItem = {
  id: string;
  name?: string | null;
  title: string;
  category: string;
  bannerImageSrc?: string | null;
  offerUrl: string;
  description?: string | null;
  tags?: readonly string[];
};

type OfferNode = {
  readonly id: string;
  readonly guid: string;
  readonly rewardLabel: string | null;
  readonly description: string | null;
  readonly paysReward: boolean;
  readonly categories: ReadonlyArray<{ readonly tag: string }>;
  readonly advertiser: {
    readonly name: string | null;
    readonly imageUrl: string | null;
  } | null;
  readonly tags: ReadonlyArray<string>;
};

export const defaultCategoryDetector: CategoryDetector = (node) =>
  node.categories[0]?.tag ?? 'allCategories';

export type UseOffersParams = {
  // GraphQL field to read results from
  apiField: 'cashbacks' | 'deals';
  // items tags
  requiredTags?: string[];
  rejectedTags?: string[];
  // promo items tags
  promoTags?: string[];
  promoRejectedTags?: string[];
  // paysReward true (cashbacks) or false (deals)
  paysReward?: boolean;
  selectedCategory: string;
  // '/cashbacks', '/deals', '/gaming'
  basePath: string;
  offerType: OfferType;
  // custom categories (for gaming)
  customCategories?: Category[];
  // detectGamingPlatform for 'gaming', defaultCategoryDetector for everything else.
  categoryDetector?: CategoryDetector;
  searchQuery?: string;
  // Relay fetch policy. 'network-only' bypasses the Relay store entirely and
  // always fetches fresh from the API. Use this when requiredTags make the query
  // unique to a page (e.g. gaming) to avoid serving cached cashbacks data.
  fetchPolicy?: 'store-or-network' | 'network-only';
};

// query covers both cashbacks and deals
// @include directives to fetch only needed fields
export const QUERY = graphql`
  query useOffersQuery(
    $requiredTags: [String!]
    $rejectedTags: [String!]
    $promoTags: [String!]
    $promoRejectedTags: [String!]
    $fetchCashbacks: Boolean!
    $fetchDeals: Boolean!
  ) {
    cashbacks(input: { requiredTags: $requiredTags, rejectedTags: $rejectedTags })
      @include(if: $fetchCashbacks) {
      edges {
        node {
          id
          guid
          title
          description
          rewardLabel
          paysReward
          advertiser {
            name
            imageUrl
          }
          categories {
            tag
            name
          }
          tags
        }
      }
    }
    cashbackPromos: cashbacks(
      input: { requiredTags: $promoTags, rejectedTags: $promoRejectedTags }
    ) @include(if: $fetchCashbacks) {
      edges {
        node {
          id
          guid
          imageUrl
          desktopImageUrl
          title
          rewardLabel
          advertiser {
            name
          }
          tags
        }
      }
    }
    deals(input: { requiredTags: $requiredTags, rejectedTags: $rejectedTags })
      @include(if: $fetchDeals) {
      edges {
        node {
          id
          guid
          title
          description
          rewardLabel
          paysReward
          advertiser {
            name
            imageUrl
          }
          categories {
            tag
            name
          }
          tags
        }
      }
    }
    dealPromos: deals(input: { requiredTags: $promoTags, rejectedTags: $promoRejectedTags })
      @include(if: $fetchDeals) {
      edges {
        node {
          id
          guid
          imageUrl
          desktopImageUrl
          title
          rewardLabel
          advertiser {
            name
          }
          tags
        }
      }
    }
  }
`;

export default function useOffers({
  apiField,
  requiredTags = [],
  rejectedTags = [],
  promoTags = [PROMOTION_TAG],
  promoRejectedTags = [],
  paysReward,
  selectedCategory,
  basePath,
  offerType,
  customCategories,
  categoryDetector,
  searchQuery = '',
  fetchPolicy = 'network-only',
}: UseOffersParams) {
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const { t } = useTranslation();
  const baseCategories = customCategories ?? OFFER_CATEGORIES;
  const titleSuffix = TITLE_SUFFIX_BY_TYPE[offerType];
  const translatedTitleSuffix = titleSuffix ? ` ${t(titleSuffix)}` : '!';
  const resolvedCategoryDetector =
    categoryDetector ?? (offerType === 'gaming' ? detectGamingPlatform : defaultCategoryDetector);

  const data = useLazyLoadQuery<useOffersQuery>(
    QUERY,
    {
      requiredTags: requiredTags.length ? requiredTags : null,
      rejectedTags: rejectedTags.length ? rejectedTags : null,
      promoTags: promoTags.length ? promoTags : null,
      promoRejectedTags: promoRejectedTags.length ? promoRejectedTags : null,
      fetchCashbacks: apiField === 'cashbacks',
      fetchDeals: apiField === 'deals',
    },
    { fetchPolicy },
  );

  const rawEdges: ReadonlyArray<{
    readonly node: OfferNode;
  }> = (apiField === 'cashbacks' ? data?.cashbacks?.edges : data?.deals?.edges) ?? [];

  const rawPromoEdges: ReadonlyArray<{
    readonly node: {
      readonly id: string;
      readonly guid: string;
      readonly imageUrl: string | null;
      readonly desktopImageUrl: string | null;
      readonly title: string;
      readonly rewardLabel: string | null;
      readonly advertiser: { readonly name: string | null } | null;
      readonly tags: ReadonlyArray<string>;
    };
  }> = (apiField === 'cashbacks' ? data?.cashbackPromos?.edges : data?.dealPromos?.edges) ?? [];

  // map raw data to OfferItems
  const allItems: OfferItem[] = rawEdges
    .filter((edge) => paysReward === undefined || edge.node.paysReward === paysReward)
    .map((edge) => {
      const titleEnd = translatedTitleSuffix;
      return {
        id: edge.node.id,
        name: edge.node.advertiser?.name,
        title:
          edge.node.rewardLabel == null
            ? ''
            : t('Earn {{rewardLabel}}{{titleEnd}}', {
                rewardLabel: edge.node.rewardLabel,
                titleEnd,
              }),
        category: resolvedCategoryDetector(edge.node),
        bannerImageSrc: edge.node.advertiser?.imageUrl,
        offerUrl: `${basePath}/${edge.node.guid}`,
        description: edge.node.description,
        tags: edge.node.tags,
      };
    });

  // category filter
  const categoryFilteredItems = useMemo(() => {
    if (selectedCategory === 'allCategories') return allItems;
    return allItems.filter((item) => item.category === selectedCategory);
  }, [allItems, selectedCategory]);

  // search
  const items = useMemo(
    () => filterBySearch(categoryFilteredItems, searchQuery),
    [categoryFilteredItems, searchQuery],
  );

  // sorted category list with counts
  const categories: CategoryOption[] = useMemo(() => {
    const translatedBase = baseCategories.map((cat) => ({ tag: cat.tag, name: t(cat.name) }));
    translatedBase.sort((a, b) => a.name.localeCompare(b.name));
    const cats = buildCategories(translatedBase, allItems);
    if (cats.length > 0) {
      cats[0] = { ...cats[0], name: t('All Categories') };
    }
    return cats;
  }, [baseCategories, allItems, t]);

  const promoItem: OfferItem | null = (() => {
    const eligible = rawPromoEdges
      .map((e) => e.node)
      .filter((promo) => {
        if (!promo?.imageUrl || !promo.desktopImageUrl) return false;
        return promoMatchesCategory(promo, selectedCategory, baseCategories);
      });

    if (eligible.length === 0) return null;

    const promo = eligible[Math.floor(Math.random() * eligible.length)];

    return {
      id: promo.id,
      name: promo.advertiser?.name,
      title: promo.rewardLabel
        ? t('Earn {{rewardLabel}} Promo{{titleSuffix}}', {
            rewardLabel: promo.rewardLabel,
            titleSuffix: translatedTitleSuffix,
          })
        : promo.title,
      category: selectedCategory,
      bannerImageSrc: isDesktop ? promo.desktopImageUrl : promo.imageUrl,
      offerUrl: `${basePath}/${promo.guid}`,
      description: promo.title,
      tags: promo.tags,
    };
  })();

  return { items, promoItem, categories };
}
